import { useState, useRef, useEffect, type ChangeEvent } from 'react'
import { Upload, Camera, X, RotateCcw, CheckCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FaceAvatar } from './FaceAvatar'

interface Props {
  open: boolean
  onClose: () => void
  userId: string
  userName: string
  proxyUrl: string
  proxyToken: string
  hasFace: boolean
  onUploaded: () => void
}

type Mode = 'idle' | 'camera' | 'captured'

export function FaceUploadModal({ open, onClose, userId, userName, proxyUrl, proxyToken, hasFace, onUploaded }: Props) {
  const [mode, setMode]               = useState<Mode>('idle')
  const [preview, setPreview]         = useState<string | null>(null)
  const [file, setFile]               = useState<File | null>(null)
  const [capturedBlob, setCapturedBlob]         = useState<Blob | null>(null)
  const [capturedPreview, setCapturedPreview]   = useState<string | null>(null)
  const [stream, setStream]           = useState<MediaStream | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [uploading, setUploading]     = useState(false)
  const inputRef    = useRef<HTMLInputElement>(null)
  const videoRef    = useRef<HTMLVideoElement>(null)
  const canvasRef   = useRef<HTMLCanvasElement>(null)
  const mountedRef  = useRef(true)

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])
  useEffect(() => { return () => { stream?.getTracks().forEach(t => t.stop()) } }, [stream])
  useEffect(() => { if (mode === 'camera' && videoRef.current && stream) videoRef.current.srcObject = stream }, [mode, stream])

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      stopStream()
      if (capturedPreview) URL.revokeObjectURL(capturedPreview)
      setMode('idle'); setPreview(null); setFile(null)
      setCapturedBlob(null); setCapturedPreview(null); setCameraError(null)
    }
  }, [open])

  function stopStream() { stream?.getTracks().forEach(t => t.stop()); setStream(null) }

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; if (!f) return
    setFile(f)
    const reader = new FileReader()
    reader.onload = () => setPreview(reader.result as string)
    reader.readAsDataURL(f)
  }

  async function compressToJpeg(source: Blob): Promise<Blob> {
    const img = new Image()
    const objectUrl = URL.createObjectURL(source)
    await new Promise<void>((resolve, reject) => { img.onload = () => resolve(); img.onerror = () => reject(); img.src = objectUrl })
    URL.revokeObjectURL(objectUrl)
    const canvas = canvasRef.current!
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight
    canvas.getContext('2d')!.drawImage(img, 0, 0)
    let quality = 0.9, blob: Blob | null = null
    while (quality >= 0.1) {
      blob = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/jpeg', quality))
      if (!blob || blob.size <= 200 * 1024) break
      quality = Math.round((quality - 0.1) * 10) / 10
    }
    if (!blob || blob.size > 200 * 1024) throw new Error('La imagen es demasiado grande para comprimir.')
    return blob
  }

  async function handleUpload() {
    const image = capturedBlob ?? file; if (!image) return
    setUploading(true)
    try {
      const jpeg = capturedBlob ?? await compressToJpeg(image)
      const res = await fetch(`${proxyUrl}/faces?userId=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${proxyToken}`, 'Content-Type': 'image/jpeg' },
        body: jpeg,
      })
      if (!res.ok) { const b = await res.json().catch(() => ({})) as { error?: string }; throw new Error(b.error ?? 'Error al subir foto') }
      toast.success('Foto facial registrada')
      onUploaded()
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al subir foto')
    } finally {
      setUploading(false)
    }
  }

  async function openCamera() {
    setCameraError(null)
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true })
      if (!mountedRef.current) { s.getTracks().forEach(t => t.stop()); return }
      setStream(s); setMode('camera')
    } catch (e) {
      const msg = e instanceof DOMException && e.name === 'NotAllowedError'
        ? 'Permiso de cámara denegado.'
        : e instanceof DOMException && e.name === 'NotFoundError'
          ? 'No se encontró ninguna cámara.'
          : 'No se pudo acceder a la cámara.'
      setCameraError(msg); toast.error(msg)
    }
  }

  function cancelCamera() {
    if (capturedPreview) URL.revokeObjectURL(capturedPreview)
    stopStream(); setCapturedBlob(null); setCapturedPreview(null); setCameraError(null); setMode('idle')
  }

  async function captureFrame() {
    const video = videoRef.current, canvas = canvasRef.current
    if (!video || !canvas) return
    canvas.width = video.videoWidth; canvas.height = video.videoHeight
    canvas.getContext('2d')!.drawImage(video, 0, 0)
    let quality = 0.9, blob: Blob | null = null
    while (quality >= 0.1) {
      blob = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/jpeg', quality))
      if (!blob) break; if (blob.size <= 200 * 1024) break
      quality = Math.round((quality - 0.1) * 10) / 10
    }
    if (!blob || blob.size > 200 * 1024) { toast.error('Imagen muy grande, acércate más.'); return }
    const prev = URL.createObjectURL(blob)
    stopStream(); setCapturedBlob(blob); setCapturedPreview(prev); setMode('captured')
  }

  async function retake() {
    if (capturedPreview) URL.revokeObjectURL(capturedPreview)
    setCapturedBlob(null); setCapturedPreview(null); setMode('idle')
    await openCamera()
  }

  const currentImage = capturedPreview ?? preview
  const hasContent = mode === 'camera' || !!currentImage

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className={`bg-surface border-border text-text-primary ${hasContent ? 'sm:max-w-2xl' : 'sm:max-w-sm'}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-accent" />
            Foto facial — {userName}
          </DialogTitle>
        </DialogHeader>

        {/* Layout: 2 columnas cuando hay contenido visual, columna única en estado vacío */}
        <div className={`py-2 ${hasContent ? 'grid grid-cols-1 md:grid-cols-2 gap-6 items-start' : 'flex flex-col items-center gap-5'}`}>

          {/* Columna izquierda / arriba: Preview o cámara */}
          <div className="flex flex-col items-center gap-3">
            {mode === 'camera' ? (
              <video ref={videoRef} autoPlay playsInline className="w-full rounded-xl border-2 border-emerald-500 object-cover" />
            ) : currentImage ? (
              <img src={currentImage} alt="preview" className="w-40 h-40 rounded-full object-cover border-2 border-accent mx-auto" />
            ) : (
              <div className="relative">
                <FaceAvatar userId={userId} name={userName} hasFace={hasFace} proxyUrl={proxyUrl} proxyToken={proxyToken} size="xl" />
                {hasFace && (
                  <span className="absolute bottom-1 right-1 bg-emerald-500 rounded-full p-0.5">
                    <CheckCircle className="w-3.5 h-3.5 text-white" />
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Columna derecha / abajo: Controles */}
          <div className="flex flex-col gap-3 w-full">
            {/* Cámara activa */}
            {mode === 'camera' && (
              <div className="flex flex-col gap-3">
                <p className="text-text-muted text-xs">Posiciona el rostro en el encuadre y captura.</p>
                <div className="flex gap-3">
                  <button onClick={cancelCamera} className="flex-1 flex items-center justify-center gap-1.5 border border-border text-text-primary text-sm px-4 py-2 rounded-md hover:bg-background transition-colors">
                    <X className="w-4 h-4" /> Cancelar
                  </button>
                  <button onClick={captureFrame} className="flex-1 flex items-center justify-center gap-1.5 bg-accent text-background text-sm font-semibold px-4 py-2 rounded-md hover:bg-accent-hover transition-colors">
                    <Camera className="w-4 h-4" /> Capturar
                  </button>
                </div>
              </div>
            )}

            {/* Idle — opciones */}
            {mode === 'idle' && !currentImage && (
              <div className="flex flex-col items-center gap-3 w-full">
                <div className="flex gap-3 w-full">
                  <button onClick={() => inputRef.current?.click()} className="flex-1 flex items-center justify-center gap-2 border border-border text-text-primary text-sm px-3 py-2.5 rounded-lg hover:bg-background transition-colors">
                    <Upload className="w-4 h-4" />
                    {hasFace ? 'Reemplazar' : 'Subir archivo'}
                  </button>
                  <button onClick={openCamera} className="flex-1 flex items-center justify-center gap-2 border border-border text-text-primary text-sm px-3 py-2.5 rounded-lg hover:bg-background transition-colors">
                    <Camera className="w-4 h-4" />
                    Usar cámara
                  </button>
                </div>
                <p className="text-text-muted text-xs">JPG o PNG — máx 200 KB</p>
                {cameraError && <p className="text-red-600 dark:text-red-400 text-xs text-center">{cameraError}</p>}
              </div>
            )}

            {/* Imagen seleccionada (archivo o captura) */}
            {currentImage && mode !== 'camera' && (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-text-primary font-medium">
                  {mode === 'captured' ? '✓ Foto capturada' : '✓ Foto seleccionada'}
                </p>
                <p className="text-text-muted text-xs">La foto se guardará en el dispositivo cuando confirmes.</p>
                <div className="flex flex-col gap-2 mt-1">
                  {mode === 'captured' && (
                    <button onClick={retake} className="flex items-center justify-center gap-1.5 border border-border text-text-primary text-sm px-3 py-2 rounded-md hover:bg-background transition-colors">
                      <RotateCcw className="w-3.5 h-3.5" /> Volver a capturar
                    </button>
                  )}
                  <button onClick={cancelCamera} className="flex items-center justify-center gap-1.5 border border-border text-text-muted text-sm px-3 py-2 rounded-md hover:bg-background transition-colors">
                    <X className="w-3.5 h-3.5" /> Cancelar
                  </button>
                  <button onClick={handleUpload} disabled={uploading} className="flex items-center justify-center gap-1.5 bg-accent text-background text-sm font-semibold px-4 py-2 rounded-md hover:bg-accent-hover disabled:opacity-60 transition-colors">
                    {uploading ? 'Subiendo...' : 'Guardar foto'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />
        <input ref={inputRef} type="file" accept="image/jpeg,image/png" onChange={handleFile} className="hidden" />
      </DialogContent>
    </Dialog>
  )
}
